import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { cn } from '@/lib/utils';
import { Check, Sparkles } from 'lucide-react';
import { CountrySelect } from './CountrySelect';
import type { AddressValue, FieldRendererProps } from './types';
import { getNestedValue, parseCompositeValue, resolveTemplateVariables } from './utils';

const DEFAULT_ADDRESS: AddressValue = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'CH',
};

/**
 * AI Suggestion Status Badge - shows status only
 */
function AISuggestionStatusBadge({
  suggestionKey,
  enrichmentContext,
  currentValue,
}: {
  suggestionKey: string;
  enrichmentContext?: Record<string, unknown>;
  currentValue: unknown;
}) {
  if (!suggestionKey) return null;

  if (!enrichmentContext || Object.keys(enrichmentContext).length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-[hsl(var(--globe-grey))]/10 text-globe-grey"
        title="AI suggestion will appear after completing the previous step"
      >
        <Sparkles className="h-3 w-3 opacity-50" />
        <span className="opacity-70">AI</span>
      </span>
    );
  }

  const suggestedValue = getNestedValue(enrichmentContext, suggestionKey);

  if (suggestedValue === undefined || suggestedValue === null) {
    return null;
  }

  const currentAddress = parseCompositeValue<AddressValue>(currentValue, DEFAULT_ADDRESS);
  const suggestedAddress = parseCompositeValue<AddressValue>(suggestedValue, DEFAULT_ADDRESS);

  // Check if all fields match
  const fieldsMatch =
    currentAddress.street === suggestedAddress.street &&
    currentAddress.city === suggestedAddress.city &&
    currentAddress.state === suggestedAddress.state &&
    currentAddress.postalCode === suggestedAddress.postalCode &&
    currentAddress.country === suggestedAddress.country;

  if (fieldsMatch) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-emerald-50 text-emerald-600 border border-emerald-200"
      >
        <Check className="h-3 w-3" />
        <span>Standard applied</span>
      </span>
    );
  }

  // No badge shown when suggestion is available but not applied
  return null;
}

/**
 * Address Field Renderer - Composite address field
 * Stores: { street, city, state, postalCode, country }
 *
 * Designed for future Google Places API integration
 */
export function AddressField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;

  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);
  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Parse composite value
  const addressValue = parseCompositeValue<AddressValue>(value, DEFAULT_ADDRESS);

  const updateField = (fieldName: keyof AddressValue, fieldValue: string) => {
    onChange(field.name, { ...addressValue, [fieldName]: fieldValue });
  };

  const handleApplySuggestion = () => {
    if (!field.aiSuggestionKey || !enrichmentContext) return;
    const suggestedValue = getNestedValue(enrichmentContext, field.aiSuggestionKey);
    if (suggestedValue) {
      const suggestedAddress = parseCompositeValue<AddressValue>(suggestedValue, DEFAULT_ADDRESS);
      onChange(field.name, suggestedAddress);
    }
  };

  // Check if there's a suggestion available
  const hasSuggestion =
    showSuggestion &&
    enrichmentContext &&
    Object.keys(enrichmentContext).length > 0 &&
    getNestedValue(enrichmentContext, field.aiSuggestionKey as string) !== undefined;

  const suggestedAddress = hasSuggestion
    ? parseCompositeValue<AddressValue>(
        getNestedValue(enrichmentContext, field.aiSuggestionKey as string),
        DEFAULT_ADDRESS
      )
    : null;

  // Check if already applied
  const isApplied =
    suggestedAddress &&
    addressValue.street === suggestedAddress.street &&
    addressValue.city === suggestedAddress.city &&
    addressValue.state === suggestedAddress.state &&
    addressValue.postalCode === suggestedAddress.postalCode &&
    addressValue.country === suggestedAddress.country;

  // Check if address is empty
  const isEmpty =
    !addressValue.street && !addressValue.city && !addressValue.state && !addressValue.postalCode;

  // Show inline button only when field is empty
  const showInlineButton = hasSuggestion && !isApplied && isEmpty;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label>
          {resolvedLabel}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey as string}
            enrichmentContext={enrichmentContext}
            currentValue={value}
          />
        )}
      </div>

      {showInlineButton && (
        <button
          type="button"
          onClick={handleApplySuggestion}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md
            bg-selise-blue/5 text-selise-blue 
            hover:bg-selise-blue/15 transition-all cursor-pointer
            border border-selise-blue/20 hover:border-selise-blue/40
            shadow-sm hover:shadow w-fit"
          title="Apply standard address"
        >
          <Sparkles className="h-3 w-3" />
          <span className="whitespace-nowrap">Use standard address</span>
        </button>
      )}

      <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
        {/* Street Address */}
        <div className="space-y-1.5">
          <Label htmlFor={`${field.name}-street`} className="text-xs text-globe-grey">
            Street Address
          </Label>
          <Input
            id={`${field.name}-street`}
            placeholder={suggestedAddress?.street || 'Street address, building, floor'}
            value={addressValue.street || ''}
            onChange={(e) => updateField('street', e.target.value)}
            className={cn(error ? 'border-destructive' : '')}
          />
        </div>

        {/* City and State */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${field.name}-city`} className="text-xs text-globe-grey">
              City
            </Label>
            <Input
              id={`${field.name}-city`}
              placeholder={suggestedAddress?.city || 'City'}
              value={addressValue.city || ''}
              onChange={(e) => updateField('city', e.target.value)}
              className={cn(error ? 'border-destructive' : '')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${field.name}-state`} className="text-xs text-globe-grey">
              State / Province / Canton
            </Label>
            <Input
              id={`${field.name}-state`}
              placeholder={suggestedAddress?.state || 'State or province'}
              value={addressValue.state || ''}
              onChange={(e) => updateField('state', e.target.value)}
              className={cn(error ? 'border-destructive' : '')}
            />
          </div>
        </div>

        {/* Postal Code and Country */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${field.name}-postalCode`} className="text-xs text-globe-grey">
              Postal / ZIP Code
            </Label>
            <Input
              id={`${field.name}-postalCode`}
              placeholder={suggestedAddress?.postalCode || 'Postal code'}
              value={addressValue.postalCode || ''}
              onChange={(e) => updateField('postalCode', e.target.value)}
              className={cn(error ? 'border-destructive' : '')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${field.name}-country`} className="text-xs text-globe-grey">
              Country
            </Label>
            <CountrySelect
              id={`${field.name}-country`}
              value={addressValue.country}
              onValueChange={(val) => updateField('country', val)}
              placeholder="Select country"
              error={!!error}
            />
          </div>
        </div>
      </div>

      {resolvedHelpText && <p className="text-xs text-globe-grey">{resolvedHelpText}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export default AddressField;
